<!-- src/views/pages/superadmin/packages/packages-list.vue -->
<script>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import { ref, computed, onMounted } from "vue";
import moment from "moment";
import DateRangePicker from "daterangepicker";
import * as bootstrap from "bootstrap";

import { usePlanStore } from "@/stores/planStore";
import planService from "@/services/super-admin/planService";
import { toast } from "@/utils/toast";
import PackagesTable from "./packages-table.vue";
import PackagesModal from "@/components/modal/packages-modal.vue";

export default {
  components: {
    PackagesTable,
    PackagesModal,
  },
  data() {
    return {
      title: "Plans",
      text: "Superadmin",
      text1: "Plans List",
    };
  },
  setup() {
    const dateRangeInput = ref(null);

    const planStore = usePlanStore();

    const selectedPlanType = ref(""); // monthly / yearly / annual_license
    const selectedStatus = ref(""); // active / inactive
    const sortOption = ref("Last 7 Days");

    const editingPlan = ref(null);
    const deletingPlan = ref(null);

    // normalized date filters from picker
    const dateFrom = ref(null);
    const dateTo = ref(null);

    const buildListParams = () => {
      const params = {};

      if (selectedStatus.value) params.status = selectedStatus.value;
      if (selectedPlanType.value) params.billing_period = selectedPlanType.value;
      if (dateFrom.value) params.date_from = dateFrom.value;
      if (dateTo.value) params.date_to = dateTo.value;

      if (sortOption.value === "Ascending") {
        params.sort_by = "name";
        params.sort_dir = "asc";
      } else if (sortOption.value === "Desending") {
        params.sort_by = "name";
        params.sort_dir = "desc";
      } else if (sortOption.value === "Recently Added") {
        params.sort_by = "created_at";
        params.sort_dir = "desc";
      }

      return params;
    };

    const booking_range = (start, end) => {
      if (dateRangeInput.value) {
        dateRangeInput.value.value =
          start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
      }

      dateFrom.value = start.startOf("day").format("YYYY-MM-DD");
      dateTo.value = end.endOf("day").format("YYYY-MM-DD");
      refreshPlans();
    };

    const toggleHeader = () => {
      document.getElementById("collapse-header")?.classList.toggle("active");
      document.body.classList.toggle("header-collapse");
    };

    const plans = computed(() => planStore.plans || []);
    const loading = computed(() => planStore.loading);

    // ---------- STATS ----------
    const totalPlans = computed(() => plans.value.length);

    const activePlans = computed(
      () =>
        plans.value.filter((p) => {
          const status = (p.status || "").toLowerCase();
          return status === "active";
        }).length
    );

    const inactivePlans = computed(
      () =>
        plans.value.filter((p) => {
          const status = (p.status || "").toLowerCase();
          return status === "inactive";
        }).length
    );

    const planTypeCount = computed(() => {
      const set = new Set(plans.value.map((p) => p.billing_period).filter(Boolean));
      return set.size;
    });

    const displayPlans = computed(() => plans.value);

    // ---------- FILTER HANDLERS ----------
    const setPlanTypeFilter = (type) => {
      selectedPlanType.value = type;
      refreshPlans();
    };

    const setStatusFilter = (status) => {
      selectedStatus.value = status;
      refreshPlans();
    };

    const setSort = (option) => {
      sortOption.value = option;

      if (option === "Last 7 Days") {
        const start = moment().subtract(7, "days");
        const end = moment();
        booking_range(start, end);
        return;
      }

      if (option === "Last Month") {
        const start = moment().subtract(1, "month").startOf("month");
        const end = moment().subtract(1, "month").endOf("month");
        booking_range(start, end);
        return;
      }

      refreshPlans();
    };

    // ---------- MODALS ----------
    const openAddModal = () => {
      editingPlan.value = null;
      const el = document.getElementById("add_plans");
      if (el) {
        const modal = bootstrap.Modal.getOrCreateInstance(el);
        modal.show();
      }
    };

    const onEditPlan = (plan) => {
      editingPlan.value = { ...plan };
      const el = document.getElementById("edit_plans");
      if (el) {
        const modal = bootstrap.Modal.getOrCreateInstance(el);
        modal.show();
      }
    };

    const onDeletePlan = (plan) => {
      deletingPlan.value = { ...plan };
      const el = document.getElementById("delete_modal");
      if (el) {
        const modal = bootstrap.Modal.getOrCreateInstance(el);
        modal.show();
      }
    };

    const clearModalState = () => {
      editingPlan.value = null;
      deletingPlan.value = null;
    };

    const syncAnnualLicensePlan = async (plan) => {
      try {
        const result = await planStore.syncAnnualLicensePlan(plan.id);
        toast(
          result?.updated_subscriptions != null
            ? `Synced ${result.updated_subscriptions} annual-license subscriptions.`
            : "Annual license settings synced.",
          "success"
        );
      } catch (error) {
        toast(
          error?.response?.data?.message || "Failed to sync annual license settings",
          "error"
        );
      }
    };

    const refreshPlans = async () => {
      await planStore.fetchPlans(buildListParams());
    };

    const downloadBlob = (blob, filename) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    };

    const exportPlans = async (format) => {
      try {
        const res = await planService.export(
          {
            status: selectedStatus.value || undefined,
            billing_period: selectedPlanType.value || undefined,
            date_from: dateFrom.value || undefined,
            date_to: dateTo.value || undefined,
          },
          format
        );

        downloadBlob(
          res.data,
          format === "pdf" ? "plans-export.pdf" : "plans-export.xlsx"
        );
      } catch (error) {
        toast(error?.response?.data?.message || "Failed to export plans", "error");
      }
    };

    onMounted(async () => {
      if (dateRangeInput.value) {
        const start = moment().subtract(6, "days");
        const end = moment();

        new DateRangePicker(
          dateRangeInput.value,
          {
            startDate: start,
            endDate: end,
            ranges: {
              Today: [moment(), moment()],
              Yesterday: [
                moment().subtract(1, "days"),
                moment().subtract(1, "days"),
              ],
              "Last 7 Days": [moment().subtract(6, "days"), moment()],
              "Last 30 Days": [moment().subtract(29, "days"), moment()],
              "This Month": [
                moment().startOf("month"),
                moment().endOf("month"),
              ],
              "Last Month": [
                moment().subtract(1, "month").startOf("month"),
                moment().subtract(1, "month").endOf("month"),
              ],
            },
          },
          booking_range
        );

        booking_range(start, end);
      } else {
        await refreshPlans();
      }
    });

    return {
      dateRangeInput,
      toggleHeader,
      // stats
      totalPlans,
      activePlans,
      inactivePlans,
      planTypeCount,
      // filters
      selectedPlanType,
      selectedStatus,
      sortOption,
      setPlanTypeFilter,
      setStatusFilter,
      setSort,
      // data & table
      plans,
      displayPlans,
      loading,
      // modal state
      editingPlan,
      deletingPlan,
      openAddModal,
      onEditPlan,
      onDeletePlan,
      clearModalState,
      refreshPlans,
      syncAnnualLicensePlan,
      exportPlans,
    };
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <!-- Page Wrapper -->
  <div class="page-wrapper dq-platform">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="me-2 mb-2">
            <div
              class="d-flex align-items-center border bg-white rounded p-1 me-2"
            >
              <router-link
                to="/super-admin/packages"
                class="btn btn-icon btn-sm active bg-primary text-white"
              >
                <i class="ti ti-list-tree"></i>
              </router-link>
            </div>
          </div>
          <div class="me-2 mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportPlans('pdf')"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportPlans('excel')"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="mb-2">
            <a
              href="javascript:void(0);"
              class="btn btn-primary d-flex align-items-center"
              @click="openAddModal"
            >
              <i class="ti ti-circle-plus me-2"></i>Add Plan
            </a>
          </div>
          <div class="ms-2 head-icons">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <!-- STATS CARDS -->
      <div class="row dq-stat-grid">
        <!-- Total Plans -->
        <div class="col-lg-3 col-md-6 d-flex">
          <div class="card flex-fill">
            <div
              class="card-body d-flex align-items-center justify-content-between"
            >
              <div class="d-flex align-items-center overflow-hidden">
                <div>
                  <p class="fs-12 fw-medium mb-1 text-truncate">Total Plans</p>
                  <h4>{{ totalPlans }}</h4>
                </div>
              </div>
              <div>
                <span class="avatar avatar-lg bg-primary flex-shrink-0">
                  <i class="ti ti-box fs-16"></i>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Active Plans -->
        <div class="col-lg-3 col-md-6 d-flex">
          <div class="card flex-fill">
            <div
              class="card-body d-flex align-items-center justify-content-between"
            >
              <div class="d-flex align-items-center overflow-hidden">
                <div>
                  <p class="fs-12 fw-medium mb-1 text-truncate">Active Plans</p>
                  <h4>{{ activePlans }}</h4>
                </div>
              </div>
              <div>
                <span class="avatar avatar-lg bg-success flex-shrink-0">
                  <i class="ti ti-activity-heartbeat fs-16"></i>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Inactive Plans -->
        <div class="col-lg-3 col-md-6 d-flex">
          <div class="card flex-fill">
            <div
              class="card-body d-flex align-items-center justify-content-between"
            >
              <div class="d-flex align-items-center overflow-hidden">
                <div>
                  <p class="fs-12 fw-medium mb-1 text-truncate">
                    Inactive Plans
                  </p>
                  <h4>{{ inactivePlans }}</h4>
                </div>
              </div>
              <div>
                <span class="avatar avatar-lg bg-danger flex-shrink-0">
                  <i class="ti ti-player-pause fs-16"></i>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Number of Plan Types -->
        <div class="col-lg-3 col-md-6 d-flex">
          <div class="card flex-fill">
            <div
              class="card-body d-flex align-items-center justify-content-between"
            >
              <div class="d-flex align-items-center overflow-hidden">
                <div>
                  <p class="fs-12 fw-medium mb-1 text-truncate">
                    No of Plan Types
                  </p>
                  <h4>{{ planTypeCount }}</h4>
                </div>
              </div>
              <div>
                <span class="avatar avatar-lg bg-skyblue flex-shrink-0">
                  <i class="ti ti-mask fs-16"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /STATS -->

      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Plan List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3"
          >
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>

            <!-- Plan Type filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Select Plan
                <span v-if="selectedPlanType">
                  &nbsp;:
                  {{
                    selectedPlanType === "monthly"
                      ? "Monthly"
                      : selectedPlanType === "yearly"
                      ? "Yearly"
                      : "Annual License"
                  }}
                </span>
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setPlanTypeFilter('monthly')"
                  >
                    Monthly
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setPlanTypeFilter('yearly')"
                  >
                    Yearly
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setPlanTypeFilter('annual_license')"
                  >
                    Annual License
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setPlanTypeFilter('')"
                  >
                    Clear
                  </a>
                </li>
              </ul>
            </div>

            <!-- Status filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Select Status
                <span v-if="selectedStatus">
                  &nbsp;:
                  {{ selectedStatus === "active" ? "Active" : "Inactive" }}
                </span>
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatusFilter('active')"
                  >
                    Active
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatusFilter('inactive')"
                  >
                    Inactive
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setStatusFilter('')"
                  >
                    Clear
                  </a>
                </li>
              </ul>
            </div>

            <!-- Sort -->
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Sort By : {{ sortOption }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('Recently Added')"
                  >
                    Recently Added
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('Ascending')"
                  >
                    Ascending
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('Desending')"
                  >
                    Desending
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('Last Month')"
                  >
                    Last Month
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('Last 7 Days')"
                  >
                    Last 7 Days
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
  <packages-table
    :plans="displayPlans"
    :loading="loading"
    @edit="onEditPlan"
    @delete="onDeletePlan"
    @sync-annual-license="syncAnnualLicensePlan"
  />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>

  <!-- Modals -->
  <packages-modal
    :plan="editingPlan"
    :delete-plan="deletingPlan"
    @plan-updated="refreshPlans"
    @modal-closed="clearModalState"
  />
</template>


